import { Button, Card, Muted, Stack, color, radius, space, status } from '@biopeak/ui';
import { useState } from 'react';
import {
  AGGREGATION_LABEL,
  OPERATOR_SYMBOL,
  RULE_STATUSES,
  sameRule,
  useAlertRules,
  useAlertRulesOf,
  useCopyAlertRules,
  useCreateAlertRule,
  useDeleteAlertRule,
  useUpdateAlertRule,
  type AlertRule,
  type AlertRuleInput,
  type MetricAggregation,
  type RuleOperator,
} from '../lib/alertRules.ts';
import { useNotificationSettings } from '../lib/communication.ts';
import { unidadeLegivel, useMetricCatalog, type MetricOption } from '../lib/patient.ts';
import { useTriage } from '../lib/triage.ts';

/**
 * Aba "Alertas" — configuração de limiares por paciente (§4.3).
 *
 * Parametrização em banco, nunca hardcoded: as métricas vêm do `metric_catalog`,
 * que é a mesma fonte que o motor da Fase 5 vai consultar para avaliar. Uma
 * lista de métricas escrita aqui divergiria do catálogo, e a que divergisse em
 * silêncio seria a da tela.
 */

const STATUS_LABEL: Record<string, string> = {
  yellow: 'Amarelo',
  red: 'Vermelho',
};

const VAZIO: AlertRuleInput = {
  metric_key: '',
  operator: 'lt',
  threshold_value: 0,
  aggregation: null,
  window_minutes: null,
  resulting_status: 'yellow',
  suppression_minutes: 60,
  active: true,
};

export function PatientAlertRules({ patientId }: { patientId: string }) {
  const rules = useAlertRules(patientId);
  const catalog = useMetricCatalog();
  const settings = useNotificationSettings();
  const [editing, setEditing] = useState<AlertRule | null>(null);
  const [creating, setCreating] = useState(false);

  const metricsByKey = new Map((catalog.data ?? []).map((m) => [m.metric_key, m]));

  return (
    <Stack gap={space.lg}>
      <MotorPendente />

      <Card>
        <Stack gap={space.md}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: space.md,
              flexWrap: 'wrap',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '19px' }}>Regras deste paciente</h2>
            {!creating && !editing && (
              <Button
                variant="secondary"
                onClick={() => setCreating(true)}
                style={{ fontSize: '15px', padding: '10px 20px' }}
              >
                Nova regra
              </Button>
            )}
          </div>

          {(creating || editing) && (
            <RuleForm
              patientId={patientId}
              metrics={catalog.data ?? []}
              defaultSuppression={settings.data?.default_suppression_minutes ?? 60}
              existing={editing}
              onDone={() => {
                setCreating(false);
                setEditing(null);
              }}
            />
          )}

          {rules.isLoading && <Muted>Carregando…</Muted>}
          {rules.data?.length === 0 && !creating && (
            <Muted style={{ fontSize: '13px' }}>Nenhuma regra configurada para este paciente.</Muted>
          )}

          <Stack gap={space.sm}>
            {(rules.data ?? []).map((r) => (
              <RuleRow
                key={r.id}
                patientId={patientId}
                rule={r}
                metric={metricsByKey.get(r.metric_key)}
                onEdit={() => {
                  setCreating(false);
                  setEditing(r);
                }}
              />
            ))}
          </Stack>
        </Stack>
      </Card>

      <CopyRules patientId={patientId} current={rules.data ?? []} metricsByKey={metricsByKey} />
    </Stack>
  );
}

/**
 * O aviso mais importante desta tela.
 *
 * O motor de avaliação é a Fase 5 e ainda não existe — nenhuma função, trigger
 * ou cron lê `alert_rules`. Um médico que configura "SpO2 < 90 → vermelho" e
 * supõe que será avisado está confiando numa rede que ainda não foi montada.
 * Isso é falha de segurança clínica, não de UX, e por isso o aviso é o primeiro
 * elemento da tela e não um rodapé.
 */
function MotorPendente() {
  return (
    <Card compact>
      <Stack gap={space.xs}>
        <span style={{ fontWeight: 600, fontSize: '15px' }}>
          Estas regras ainda não estão sendo avaliadas
        </span>
        <Muted style={{ fontSize: '13px' }}>
          O motor que compara os dados recebidos contra os limiares é a próxima fase do projeto.
          Até lá, uma regra salva aqui fica registrada mas <strong>não dispara alerta nenhum</strong>
          . Configure à vontade — nada será perdido —, mas não conte com ela para ser avisado.
        </Muted>
      </Stack>
    </Card>
  );
}

// -------------------------------------------------------------- listagem

/**
 * Janela da regra, em DIAS.
 *
 * O schema guarda minutos e continua guardando — o motor já sabe comparar
 * janela contra coluna de data. O que mudou foi o posicionamento: com o dado
 * bruto de alta frequência fora do ar, não existe mais série que responda "FC
 * acima de 120 por 10 minutos". Toda regra agora é tendência sobre dado
 * diário, e oferecer minutos convidaria a configurar o que não funciona.
 */
const MINUTOS_POR_DIA = 1440;

function janelaEmDias(minutos: number | null): number | null {
  return minutos == null ? null : Math.round(minutos / MINUTOS_POR_DIA);
}

function frase(rule: AlertRule, metric?: MetricOption): string {
  const nome = metric?.display_name ?? rule.metric_key;
  const u = unidadeLegivel(metric);
  const unidade = u ? ` ${u}` : '';
  const agg = rule.aggregation ? `${AGGREGATION_LABEL[rule.aggregation]} de ` : '';
  const dias = janelaEmDias(rule.window_minutes);
  const janela = dias ? ` nos últimos ${dias} ${dias === 1 ? 'dia' : 'dias'}` : '';
  return `${agg}${nome} ${OPERATOR_SYMBOL[rule.operator]} ${rule.threshold_value}${unidade}${janela}`;
}

function RuleRow({
  patientId,
  rule,
  metric,
  onEdit,
}: {
  patientId: string;
  rule: AlertRule;
  metric?: MetricOption;
  onEdit: () => void;
}) {
  const remove = useDeleteAlertRule(patientId);
  const update = useUpdateAlertRule(patientId);
  const [confirmando, setConfirmando] = useState(false);

  const tone = rule.resulting_status === 'red' ? status.red : status.yellow;

  return (
    <Stack
      gap={space.sm}
      style={{
        borderTop: `1px solid ${color.border}`,
        paddingTop: space.sm,
        opacity: rule.active ? 1 : 0.5,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: space.md,
          flexWrap: 'wrap',
        }}
      >
        <Stack gap={space.xs} style={{ minWidth: 0 }}>
          <span style={{ fontSize: '15px' }}>
            {frase(rule, metric)} <span style={{ color: color.textSecondary }}>→</span>{' '}
            <span style={{ color: tone.fg, fontWeight: 600 }}>
              {STATUS_LABEL[rule.resulting_status]}
            </span>
          </span>
          <Muted style={{ fontSize: '12px' }}>
            silêncio de {rule.suppression_minutes} min entre disparos
            {!rule.active && ' · desativada'}
          </Muted>
        </Stack>

        <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap' }}>
          <Button
            variant="quiet"
            onClick={() => update.mutate({ id: rule.id, patch: { active: !rule.active } })}
            style={{ fontSize: '14px', padding: '8px 14px' }}
          >
            {rule.active ? 'Desativar' : 'Ativar'}
          </Button>
          <Button variant="quiet" onClick={onEdit} style={{ fontSize: '14px', padding: '8px 14px' }}>
            Editar
          </Button>
          {confirmando ? (
            <Button
              onClick={() => remove.mutate(rule.id)}
              disabled={remove.isPending}
              style={{ fontSize: '14px', padding: '8px 14px' }}
            >
              {remove.isPending ? 'Removendo…' : 'Confirmar'}
            </Button>
          ) : (
            <Button
              variant="quiet"
              onClick={() => setConfirmando(true)}
              style={{ fontSize: '14px', padding: '8px 14px' }}
            >
              Remover
            </Button>
          )}
        </div>
      </div>

      {/* Uma regra que já disparou não pode ser apagada — o histórico do
          paciente depende dela para explicar o que aconteceu. A recusa vem do
          banco (23503); aqui ela vira uma frase que diz qual é a saída. */}
      {remove.isError && (
        <Muted style={{ fontSize: '13px', color: status.red.fg }}>
          {(remove.error as Error).message}
        </Muted>
      )}
    </Stack>
  );
}

// ------------------------------------------------------------ formulário

function RuleForm({
  patientId,
  metrics,
  defaultSuppression,
  existing,
  onDone,
}: {
  patientId: string;
  metrics: MetricOption[];
  defaultSuppression: number;
  existing: AlertRule | null;
  onDone: () => void;
}) {
  const create = useCreateAlertRule(patientId);
  const update = useUpdateAlertRule(patientId);

  const [form, setForm] = useState<AlertRuleInput>(() =>
    existing
      ? {
          metric_key: existing.metric_key,
          operator: existing.operator,
          threshold_value: Number(existing.threshold_value),
          aggregation: existing.aggregation,
          window_minutes: existing.window_minutes,
          resulting_status: existing.resulting_status as 'yellow' | 'red',
          suppression_minutes: existing.suppression_minutes,
          active: existing.active,
        }
      : { ...VAZIO, suppression_minutes: defaultSuppression },
  );

  const metric = metrics.find((m) => m.metric_key === form.metric_key);
  const invalido = !form.metric_key || !Number.isFinite(form.threshold_value);
  const salvando = create.isPending || update.isPending;
  const erro = (create.error ?? update.error) as Error | undefined;

  function salvar() {
    if (existing) {
      update.mutate({ id: existing.id, patch: form }, { onSuccess: onDone });
    } else {
      create.mutate(form, { onSuccess: onDone });
    }
  }

  return (
    <div
      style={{
        border: `1px solid ${color.border}`,
        borderRadius: radius.card,
        padding: space.md,
      }}
    >
      <Stack gap={space.md}>
        <span style={{ fontWeight: 600 }}>{existing ? 'Editar regra' : 'Nova regra'}</span>

        <div style={{ display: 'flex', gap: space.md, flexWrap: 'wrap' }}>
          <Campo label="Métrica">
            <select
              value={form.metric_key}
              onChange={(e) => setForm({ ...form, metric_key: e.target.value })}
              style={inputStyle}
            >
              <option value="">selecione…</option>
              {metrics.map((m) => (
                <option key={m.metric_key} value={m.metric_key}>
                  {m.display_name}
                </option>
              ))}
            </select>
          </Campo>

          <Campo label="Operador">
            <select
              value={form.operator}
              onChange={(e) => setForm({ ...form, operator: e.target.value as RuleOperator })}
              style={inputStyle}
            >
              {(Object.keys(OPERATOR_SYMBOL) as RuleOperator[]).map((op) => (
                <option key={op} value={op}>
                  {OPERATOR_SYMBOL[op]}
                </option>
              ))}
            </select>
          </Campo>

          <Campo label={`Valor${metric ? ` (${unidadeLegivel(metric)})` : ''}`}>
            <input
              type="number"
              step="any"
              value={form.threshold_value}
              onChange={(e) => setForm({ ...form, threshold_value: Number(e.target.value) })}
              style={inputStyle}
            />
          </Campo>

          <Campo label="Resultado">
            <select
              value={form.resulting_status}
              onChange={(e) =>
                setForm({ ...form, resulting_status: e.target.value as 'yellow' | 'red' })
              }
              style={inputStyle}
            >
              {RULE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </Campo>
        </div>

        <div style={{ display: 'flex', gap: space.md, flexWrap: 'wrap' }}>
          <Campo
            label="Agregação"
            hint={
              metric ? 'vazio = usa o padrão da métrica' : 'escolha a métrica para ver o padrão'
            }
          >
            <select
              value={form.aggregation ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  aggregation: e.target.value ? (e.target.value as MetricAggregation) : null,
                })
              }
              style={inputStyle}
            >
              <option value="">padrão da métrica</option>
              {(Object.keys(AGGREGATION_LABEL) as MetricAggregation[]).map((a) => (
                <option key={a} value={a}>
                  {AGGREGATION_LABEL[a]}
                </option>
              ))}
            </select>
          </Campo>

          <Campo label="Janela (dias)" hint="vazio = apenas o dado mais recente">
            <input
              type="number"
              min={1}
              value={janelaEmDias(form.window_minutes) ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  window_minutes: e.target.value
                    ? Number(e.target.value) * MINUTOS_POR_DIA
                    : null,
                })
              }
              style={inputStyle}
            />
          </Campo>

          <Campo label="Silêncio (min)" hint="evita repetir o alerta a cada oscilação">
            <input
              type="number"
              min={0}
              value={form.suppression_minutes}
              onChange={(e) => setForm({ ...form, suppression_minutes: Number(e.target.value) })}
              style={inputStyle}
            />
          </Campo>
        </div>

        {erro && (
          <Muted style={{ color: status.red.fg, fontSize: '13px' }}>{erro.message}</Muted>
        )}

        <div style={{ display: 'flex', gap: space.sm, justifyContent: 'flex-end' }}>
          <Button
            variant="secondary"
            onClick={onDone}
            style={{ fontSize: '15px', padding: '10px 20px' }}
          >
            Cancelar
          </Button>
          <Button
            disabled={invalido || salvando}
            onClick={salvar}
            style={{ fontSize: '15px', padding: '10px 20px' }}
          >
            {salvando ? 'Salvando…' : 'Salvar regra'}
          </Button>
        </div>
      </Stack>
    </div>
  );
}

// ---------------------------------------------------------- duplicação

/**
 * Importar as regras de outro paciente (§4.3).
 *
 * IMPORTAR, não exportar: a ação acontece na tela do paciente que vai receber,
 * e o médico vê o resultado onde está. Um botão de "exportar para outro
 * paciente" escreveria numa ficha que não está à vista, o que é justamente o
 * tipo de escrita cega que um prontuário não deve permitir.
 */
function CopyRules({
  patientId,
  current,
  metricsByKey,
}: {
  patientId: string;
  current: AlertRule[];
  metricsByKey: Map<string, MetricOption>;
}) {
  const triage = useTriage();
  const [origem, setOrigem] = useState<string>('');
  const origemRules = useAlertRulesOf(origem || null);
  const copy = useCopyAlertRules(patientId);

  const outros = (triage.data ?? []).filter((t) => t.patient_id !== patientId);
  const candidatas = origemRules.data ?? [];

  return (
    <Card>
      <Stack gap={space.md}>
        <Stack gap={space.xs}>
          <h2 style={{ margin: 0, fontSize: '19px' }}>Importar regras de outro paciente</h2>
          <Muted style={{ fontSize: '13px' }}>
            Copia a configuração de outro paciente seu para este. As regras já existentes aqui
            continuam como estão — a importação acrescenta, não substitui.
          </Muted>
        </Stack>

        <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={origem} onChange={(e) => setOrigem(e.target.value)} style={inputStyle}>
            <option value="">selecione o paciente de origem…</option>
            {outros.map((t) => (
              <option key={t.patient_id} value={t.patient_id ?? ''}>
                {t.full_name}
              </option>
            ))}
          </select>
        </div>

        {origem && origemRules.isLoading && <Muted>Carregando regras…</Muted>}

        {origem && !origemRules.isLoading && candidatas.length === 0 && (
          <Muted style={{ fontSize: '13px' }}>Esse paciente não tem regras configuradas.</Muted>
        )}

        {candidatas.length > 0 && (
          <Stack gap={space.sm}>
            <Muted style={{ fontSize: '13px' }}>
              Serão criadas {candidatas.length}{' '}
              {candidatas.length === 1 ? 'regra' : 'regras'} neste paciente:
            </Muted>
            {candidatas.map((r) => {
              const repetida = current.some((c) => sameRule(c, r));
              return (
                <div key={r.id} style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '14px' }}>
                    {frase(r, metricsByKey.get(r.metric_key))} →{' '}
                    {STATUS_LABEL[r.resulting_status]}
                  </span>
                  {/* O banco não impede duplicar; quem impede é o médico ver
                      antes que já existe uma igual. */}
                  {repetida && (
                    <span style={{ fontSize: '12px', color: color.textSecondary }}>
                      · já existe uma igual aqui
                    </span>
                  )}
                </div>
              );
            })}

            {copy.isError && (
              <Muted style={{ color: status.red.fg, fontSize: '13px' }}>
                {(copy.error as Error).message}
              </Muted>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                disabled={copy.isPending}
                onClick={() => copy.mutate(candidatas, { onSuccess: () => setOrigem('') })}
                style={{ fontSize: '15px', padding: '10px 20px' }}
              >
                {copy.isPending ? 'Importando…' : `Importar ${candidatas.length}`}
              </Button>
            </div>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

// ------------------------------------------------------------ auxiliares

function Campo({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <Stack gap={space.xs} style={{ minWidth: '150px' }}>
      <span style={{ fontSize: '14px', fontWeight: 500 }}>{label}</span>
      {children}
      {hint && <Muted style={{ fontSize: '12px' }}>{hint}</Muted>}
    </Stack>
  );
}

const inputStyle: React.CSSProperties = {
  border: `1px solid ${color.border}`,
  borderRadius: radius.input,
  padding: '8px 12px',
  fontSize: '15px',
  fontFamily: 'inherit',
  background: color.white,
  color: color.text,
};
